import { useTranslation } from '@presentation/utils';
import { useFormikContext } from 'formik';
import type { FC } from 'react';
import { InfoBox, InputFileField, InputLabel, InputWrapper } from '@presentation/components';
import { CountryCode } from '@gql/graphql';
import { FaFile } from 'react-icons/fa';
import * as Styled from '../styled';
import type { MtBreederOnboardingFormValues, MtBreederOnboardingPageProps } from '../types';

export const Step12: FC<
  Pick<MtBreederOnboardingPageProps, 'showGermanFileFields' | 'showCertificationFileField' | 'log'>
> = (props) => {
  const { showGermanFileFields, showCertificationFileField, log } = props;
  const { values } = useFormikContext<MtBreederOnboardingFormValues>();
  const { translate } = useTranslation();

  return (
    <Styled.Step12MainContainer>
      {showGermanFileFields ? (
        <>
          <InputWrapper split>
            <InputLabel>{translate('mt.breeder.onboarding.permissionFiles.label')}</InputLabel>
            <InfoBox>{translate('mt.breeder.onboarding.permissionFiles.warning')}</InfoBox>
            <InputWrapper>
              <Styled.MultipleFilesWrapper>
                <InputFileField
                  name="permissionFiles"
                  icon={<FaFile />}
                  placeholder={`${translate(
                    'mt.breeder.onboarding.permissionFiles.inputPlaceholder',
                  )}\n Max 8 MB`}
                  accept=".pdf, .doc., image/*, .heic, .heif"
                  isImage={false}
                  multiple
                  log={log}
                />
              </Styled.MultipleFilesWrapper>
            </InputWrapper>
          </InputWrapper>

          <InputWrapper split>
            <InputLabel>{translate('mt.breeder.onboarding.identityFiles.label')}</InputLabel>
            <InfoBox>{translate('mt.breeder.onboarding.identityFiles.warning')}</InfoBox>
            <InputWrapper>
              <Styled.MultipleFilesWrapper>
                <InputFileField
                  log={log}
                  name="identityFiles"
                  icon={<FaFile />}
                  placeholder={`${translate(
                    'mt.breeder.onboarding.identityFiles.inputPlaceholder',
                  )}\n Max 8 MB`}
                  accept=".pdf, .doc., image/*, .heic, .heif"
                  isImage={false}
                  multiple
                />
              </Styled.MultipleFilesWrapper>
            </InputWrapper>
          </InputWrapper>
        </>
      ) : (
        <InputWrapper split>
          <InputLabel required={values.country === CountryCode.NL}>
            {translate(`mt.breeder.onboarding.agreementFile.label.${values.country}`)}
          </InputLabel>
          <InputWrapper>
            {[CountryCode.FR].includes(values.country as CountryCode) ? (
              <InfoBox>{translate('mt.breeder.onboarding.agreementFile.warning')}</InfoBox>
            ) : null}
            {[CountryCode.NL, CountryCode.BE].includes(values.country as CountryCode) ? (
              <InfoBox>
                {translate(`mt.breeder.onboarding.agreementFile.requiredText.${values.country}`, {
                  defaultValue: '',
                })}
              </InfoBox>
            ) : (
              ''
            )}
            <Styled.SingleFileWrapper>
              <InputFileField
                log={log}
                name="hkFile"
                icon={<FaFile />}
                placeholder={`${translate(
                  'mt.breeder.onboarding.agreementFile.inputPlaceholder',
                )}\n Max 8 MB`}
                accept=".pdf, .doc., image/*, .heic, .heif"
                isImage={false}
              />
            </Styled.SingleFileWrapper>
          </InputWrapper>
        </InputWrapper>
      )}

      <InputWrapper split>
        <InputLabel>{translate('mt.breeder.onboarding.breedClubFiles.label')}</InputLabel>
        <InfoBox>{translate('mt.breeder.onboarding.breedClubFiles.warning')}</InfoBox>
        <InputWrapper>
          <Styled.MultipleFilesWrapper>
            <InputFileField
              log={log}
              name="breedClubFiles"
              icon={<FaFile />}
              placeholder={`${translate(
                'mt.breeder.onboarding.breedClubFiles.inputPlaceholder',
              )}\n Max 8 MB`}
              accept=".pdf, .doc., image/*, .heic, .heif"
              isImage={false}
              multiple
            />
          </Styled.MultipleFilesWrapper>
        </InputWrapper>
      </InputWrapper>

      {showCertificationFileField && (
        <>
          <InputWrapper split />
          <InputWrapper split>
            <InputLabel>{translate('mt.breeder.onboarding.certificationFile.label')}</InputLabel>
            <InputWrapper>
              <InfoBox>{translate('mt.breeder.onboarding.certificationFile.warning')}</InfoBox>
              <Styled.SingleFileWrapper>
                <InputFileField
                  log={log}
                  name="certificationFile"
                  icon={<FaFile />}
                  placeholder={`${translate(
                    'mt.breeder.onboarding.certificationFile.inputPlaceholder',
                  )}\n Max 8 MB`}
                  accept=".pdf, .doc., image/*, .heic, .heif"
                  isImage={false}
                />
              </Styled.SingleFileWrapper>
            </InputWrapper>
          </InputWrapper>
        </>
      )}
    </Styled.Step12MainContainer>
  );
};
